import { useEffect, useState, type ReactElement } from 'react';
import { useSession } from './SessionProvider';
import './IdentifiedLinkConfirm.css';

/**
 * Post-redirect identity confirmation.
 *
 * The /r/<token> handler 302-redirects with `?ori_signed_in=1` and
 * `?ori_hint=<last4>` appended to the destination. On first paint we
 * detect those params, strip them from the URL via history.replaceState
 * (so a refresh / share doesn't re-show the modal), and display a
 * non-blocking dialog that lets the recipient confirm "yes, that's me"
 * or escape with "Not me — log out". This is the safety net for the
 * "user copied the link before tapping" attack vector: the wrong
 * person sees a name they don't recognize and has a one-click exit.
 *
 * No legacy `?_a=` exchange happens here anymore — the server does
 * the cookie set during the redirect; the SPA just reads its identity
 * via /api/me and shows this confirmation as a layer on top.
 */
export function IdentifiedLinkConsumer(): ReactElement | null {
  const { session, status, logout } = useSession();
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const [hint, setHint] = useState('');

  // Mount-once: detect the redirect flag, strip from URL, remember
  // that we owe the user a confirmation. Strip happens regardless of
  // session status so a refresh / share doesn't replay the modal.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const signedIn = params.get('ori_signed_in') === '1';
    const fromHint = String(params.get('ori_hint') || '').slice(0, 16);
    if (!signedIn) return;
    params.delete('ori_signed_in');
    params.delete('ori_hint');
    const next = `${window.location.pathname}${
      params.toString() ? `?${params.toString()}` : ''
    }${window.location.hash}`;
    window.history.replaceState({}, '', next);
    setHint(fromHint);
    setPendingConfirm(true);
  }, []);

  if (!pendingConfirm || status !== 'authenticated' || !session) return null;

  const display = session.self_name?.trim() || 'someone';
  const onConfirm = () => {
    setPendingConfirm(false);
  };
  const onEscape = () => {
    setPendingConfirm(false);
    void (async () => {
      await logout();
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    })();
  };

  return (
    <div className="ori-confirm-backdrop" role="dialog" aria-modal="true" data-log-seen="link_confirm">
      <div className="ori-confirm-card">
        <div className="ori-confirm-spark">✦</div>
        <div className="ori-confirm-eyebrow">You're signed in as</div>
        <div className="ori-confirm-name">
          {display}
          {hint && <span className="ori-confirm-hint"> · ●●●●{hint}</span>}
        </div>
        <p className="ori-confirm-body">
          We picked you up from the link you tapped. If this isn't you,
          log out and re-enter your details.
        </p>
        <div className="ori-confirm-actions">
          <button type="button" className="ori-confirm-yes" data-log="link_confirm_me" onClick={onConfirm}>
            That's me, continue
          </button>
          <button type="button" className="ori-confirm-no" data-log="link_confirm_not_me" onClick={onEscape}>
            Not me — log out
          </button>
        </div>
      </div>
    </div>
  );
}
